import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tab,
  Tabs,
  Chip,
  Grid,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAnalytics } from '../utils/analytics';

interface PayoutRequest {
  id: number;
  creator_id: string;
  amount: number;
  status: string;
  requested_at: string;
  updated_at: string | null;
  paypal_email: string;
}

interface PayPalTransaction {
  id: string;
  digitalproducts_id: string;
  creator_id: string;
  buyer_email: string;
  price: number;
  payment_status: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface TransactionStats {
  totalTransactions: number;
  totalRevenue: number;
  avgTransactionValue: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [pendingRequests, setPendingRequests] = useState<PayoutRequest[]>([]);
  const [approvedRequests, setApprovedRequests] = useState<PayoutRequest[]>([]);
  const [transactions, setTransactions] = useState<PayPalTransaction[]>([]);
  const [transactionStats, setTransactionStats] = useState<TransactionStats>({ 
    totalTransactions: 0, 
    totalRevenue: 0, 
    avgTransactionValue: 0 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');

  useEffect(() => {
    fetchPayoutRequests();
    fetchTransactionData();
    setupSubscription();
    
    // Track admin dashboard access
    trackEvent('admin_dashboard_accessed', {
      timestamp: new Date().toISOString()
    });
  }, []);

  const fetchPayoutRequests = async () => {
    try {
      // Fetch pending requests
      const { data: pendingData, error: pendingError } = await supabase
        .from('payout_requests')
        .select('*')
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (pendingError) throw pendingError;

      // Fetch approved requests
      const { data: approvedData, error: approvedError } = await supabase
        .from('payout_requests')
        .select('*')
        .eq('status', 'approved')
        .order('requested_at', { ascending: false });

      if (approvedError) throw approvedError;

      setPendingRequests(pendingData || []);
      setApprovedRequests(approvedData || []);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const fetchTransactionData = async () => {
    try {
      // Fetch PayPal transactions
      const { data: transactionData, error: transactionError } = await supabase
        .from('digitalproducts_purchases')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (transactionError) throw transactionError;

      setTransactions(transactionData || []);

      // Calculate stats
      const totalTransactions = transactionData?.length || 0;
      const completedTransactions = transactionData?.filter(t => t.payment_status === 'completed') || [];
      const totalRevenue = completedTransactions.reduce((sum, t) => sum + t.price, 0);
      const avgTransactionValue = completedTransactions.length > 0 ? totalRevenue / completedTransactions.length : 0;

      setTransactionStats({
        totalTransactions,
        totalRevenue,
        avgTransactionValue
      });

    } catch (error: any) {
      console.error('Error fetching transaction data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupSubscription = () => {
    supabase
      .channel('payout_requests_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'payout_requests' },
        () => {
          fetchPayoutRequests();
        }
      )
      .subscribe();
  };

  const handleApprove = async (requestId: number) => {
    try {
      const { error } = await supabase
        .from('payout_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Track payout approval
      trackEvent('payout_approved', {
        payout_request_id: requestId,
        timestamp: new Date().toISOString()
      });

      // Optimistically update the UI
      const approvedRequest = pendingRequests.find(req => req.id === requestId);
      if (approvedRequest) {
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        setApprovedRequests(prev => [{ 
          ...approvedRequest, 
          status: 'approved',
          updated_at: new Date().toISOString()
        }, ...prev]);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredApprovedRequests = approvedRequests.filter((request) =>
    request.creator_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.paypal_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTransactions = transactions.filter((transaction) =>
    transaction.buyer_email.toLowerCase().includes(transactionSearchTerm.toLowerCase()) ||
    transaction.title.toLowerCase().includes(transactionSearchTerm.toLowerCase()) ||
    transaction.payment_status.toLowerCase().includes(transactionSearchTerm.toLowerCase())
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Track tab navigation
    const tabNames = ['payouts', 'transactions', 'analytics'];
    trackEvent('admin_tab_change', {
      tab: tabNames[newValue],
      timestamp: new Date().toISOString()
    });
  };

  // Prepare chart data
  const getChartData = () => {
    const dailyData = transactions.reduce((acc, transaction) => {
      const date = new Date(transaction.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, revenue: 0, count: 0 };
      }
      if (transaction.payment_status === 'completed') {
        acc[date].revenue += transaction.price;
        acc[date].count += 1;
      }
      return acc;
    }, {} as Record<string, { date: string; revenue: number; count: number }>);

    return Object.values(dailyData).slice(-30); // Last 30 days
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      color: '#000000'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSignOut}
        >
          Sign Out
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography color="#666666" gutterBottom variant="subtitle2">
                Total Transactions
              </Typography>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {transactionStats.totalTransactions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography color="#666666" gutterBottom variant="subtitle2">
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                ${transactionStats.totalRevenue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography color="#666666" gutterBottom variant="subtitle2">
                Avg Transaction
              </Typography>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                ${transactionStats.avgTransactionValue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography color="#666666" gutterBottom variant="subtitle2">
                Active Products
              </Typography>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {transactions.filter(t => t.payment_status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: '2px solid #e0e0e0', mb: 3, backgroundColor: '#ffffff' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#666666',
              fontWeight: 'bold',
              '&.Mui-selected': {
                color: '#000000'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
              height: '3px'
            }
          }}
        >
          <Tab label="Payout Requests" />
          <Tab label="PayPal Transactions" />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      {/* Tab 0: Payout Requests */}
      {activeTab === 0 && (
        <>
          <Card sx={{ mb: 4, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
                Pending Payout Requests
              </Typography>
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Creator ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>PayPal Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Requested Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#666666', py: 4 }}>
                          No pending requests
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingRequests.map((request) => (
                        <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                          <TableCell sx={{ color: '#000000' }}>{request.creator_id}</TableCell>
                          <TableCell sx={{ color: '#000000' }}>{request.paypal_email}</TableCell>
                          <TableCell sx={{ color: '#000000', fontWeight: 'bold' }}>${request.amount}</TableCell>
                          <TableCell sx={{ color: '#000000' }}>
                            {new Date(request.requested_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleApprove(request.id)}
                              sx={{ fontWeight: 'bold' }}
                            >
                              Approve
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
                Approved Payout Requests
              </Typography>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Search by Creator ID or PayPal Email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '& fieldset': { borderColor: '#e0e0e0' }
                    },
                    '& .MuiInputLabel-root': { color: '#666666' }
                  }}
                />
              </Box>
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Creator ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>PayPal Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#000000' }}>Requested Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredApprovedRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#666666', py: 4 }}>
                          No approved requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredApprovedRequests.map((request) => (
                        <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                          <TableCell sx={{ color: '#000000' }}>{request.creator_id}</TableCell>
                          <TableCell sx={{ color: '#000000' }}>{request.paypal_email}</TableCell>
                          <TableCell sx={{ color: '#000000', fontWeight: 'bold' }}>${request.amount}</TableCell>
                          <TableCell sx={{ color: '#000000' }}>
                            {new Date(request.requested_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
      {/* Tab 1: PayPal Transactions */}
      {activeTab === 1 && (
        <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
              PayPal Transactions
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search by Buyer Email, Product Title, or Status"
                value={transactionSearchTerm}
                onChange={(e) => setTransactionSearchTerm(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '& fieldset': { borderColor: '#e0e0e0' }
                  },
                  '& .MuiInputLabel-root': { color: '#666666' }
                }}
              />
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 600, backgroundColor: '#ffffff' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: '#000000', backgroundColor: '#f5f5f5' }}>Product Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#000000', backgroundColor: '#f5f5f5' }}>Buyer Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#000000', backgroundColor: '#f5f5f5' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#000000', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#000000', backgroundColor: '#f5f5f5' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: '#666666', py: 4 }}>
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.slice(0, 100).map((transaction) => (
                      <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#000000' }}>
                            {transaction.title}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#000000' }}>{transaction.buyer_email}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000' }}>
                            ${transaction.price.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.payment_status}
                            color={transaction.payment_status === 'completed' ? 'success' : 
                                   transaction.payment_status === 'pending' ? 'warning' : 'error'}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#000000' }}>
                          {new Date(transaction.created_at).toLocaleDateString()} {' '}
                          {new Date(transaction.created_at).toLocaleTimeString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredTransactions.length > 100 && (
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                Showing first 100 transactions. Use search to filter results.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Analytics */}
      {activeTab === 2 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', mb: 3 }}>
                    Daily Revenue (Last 30 Days)
                  </Typography>
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart data={getChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#000000', fontSize: 12 }}
                        axisLine={{ stroke: '#000000' }}
                      />
                      <YAxis 
                        tick={{ fill: '#000000', fontSize: 12 }}
                        axisLine={{ stroke: '#000000' }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'revenue' ? `$${value}` : value,
                          name === 'revenue' ? 'Revenue' : 'Transactions'
                        ]}
                        contentStyle={{ 
                          backgroundColor: '#ffffff', 
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          color: '#000000'
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#000000' }}
                      />
                      <Bar dataKey="revenue" fill="#4caf50" name="Revenue" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="count" fill="#2196f3" name="Transactions" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
                    Revenue Summary
                  </Typography>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body1" sx={{ color: '#666666', mb: 1 }}>
                      Total Revenue: <strong style={{ color: '#000000' }}>${transactionStats.totalRevenue.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666666', mb: 1 }}>
                      Total Transactions: <strong style={{ color: '#000000' }}>{transactionStats.totalTransactions}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666666' }}>
                      Average Transaction: <strong style={{ color: '#000000' }}>${transactionStats.avgTransactionValue.toFixed(2)}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}